import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
    Check as CheckIcon,
    Close as CloseIcon,
    Warning as WarningIcon,
    DoneAll as DoneAllIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatBuyerColumnDisplay, parseDeliveryDateForUi } from '../utils/invoiceUtils';

const getCurrencySymbol = (currency) => {
    switch (currency) {
        case 'USD':
            return '$';
        case 'KRW':
            return '₩';
        case 'IDR':
            return 'Rp';
        default:
            return currency;
    }
};

const parseSizeFromThreadName = (threadName = '') => {
    const match = String(threadName).match(/\[([^\]]+)\]/);
    if (!match || !match[1]) return '';
    return match[1].replace(/,/g, '').trim();
};

/**
 * 팝업 납품일 기본값: GI REQ 날짜 (gi_request_planned_dispatch_at)
 */
const getDefaultDeliveryDateForInvoicePopup = (item) => {
    return parseDeliveryDateForUi(item.gi_request_planned_dispatch_at);
};

const InvoiceIssuanceDialog = ({
    open,
    onClose,
    onConfirm,
    isSubmitting = false,
    selectedItems,
    invoiceData,
    t
}) => {
    const { i18n } = useTranslation();
    const [vatOption, setVatOption] = useState('tax_free'); // 면세, BC21, BC45
    const [bulkDeliveryDate, setBulkDeliveryDate] = useState(null);
    const [editableDeliveryDates, setEditableDeliveryDates] = useState({});
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const deliveryDateFormat = i18n.language === 'ko' ? 'YYYY.MM.DD' : i18n.language === 'id' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';

    const allSelectedData = useMemo(
        () => invoiceData.filter((item) => selectedItems.includes(item.id)),
        [invoiceData, selectedItems]
    );

    const selectedData = useMemo(() => {
        if (!selectedRowKeys || selectedRowKeys.length === 0) return [];
        const keySet = new Set(selectedRowKeys);
        return allSelectedData.filter((item) => {
            const rowKey = item.id || `${item.main_order_id}-${item.sub_order_id}`;
            return keySet.has(rowKey);
        });
    }, [allSelectedData, selectedRowKeys]);

    const getHistoryQtySum = (item) => {
        if (!item || !Array.isArray(item.gi_history) || item.gi_history.length === 0) return 0;
        return item.gi_history.reduce((sum, h) => sum + (parseFloat(h?.qty) || 0), 0);
    };

    // 팝업 기준 납품 수량: 납품일자(gi_history) 합계 우선, 없으면 테이블 납품수량(qty_requested)
    const getPopupQty = (item) => {
        const historySum = getHistoryQtySum(item);
        if (historySum > 0) return historySum;
        return item?.qty_requested ?? 0;
    };

    const detailRows = useMemo(() => (
        allSelectedData.map((item) => {
            const rowKey = item.id || `${item.main_order_id}-${item.sub_order_id}`;
            const initialDate = getDefaultDeliveryDateForInvoicePopup(item);
            return {
                rowKey,
                item,
                initialDate
            };
        })
    ), [allSelectedData]);

    const seededForOpenRef = useRef(false);
    useEffect(() => {
        if (!open) {
            seededForOpenRef.current = false;
            return;
        }

        if (seededForOpenRef.current) return;
        seededForOpenRef.current = true;

        const initialDates = {};
        detailRows.forEach((row) => {
            initialDates[row.rowKey] = row.initialDate;
        });
        setEditableDeliveryDates(initialDates);
        setSelectedRowKeys(detailRows.map((row) => row.rowKey));
        setBulkDeliveryDate(null);
    }, [open, detailRows]);
    
    // 선택된 데이터를 그룹화하여 인보이스별로 분류
    const invoiceGroups = allSelectedData.reduce((groups, item) => {
        const key = `${item.main_order_id}-${item.vendor_name}-${item.manager_name}`;
        if (!groups[key]) {
            groups[key] = {
                mainOrderId: item.main_order_id,
                vendorName: item.vendor_name,
                managerName: item.manager_name,
                currency: item.price_currency,
                amount: 0,
                vatAmount: 0,
                vatAmount2: 0,
                items: []
            };
        }
        
        const effectiveQty = getPopupQty(item);
        const itemAmount = effectiveQty * (item.price || 0);
        groups[key].amount += itemAmount;
        
        // VAT 계산 - BC21은 vatAmount, BC45는 vatAmount2에 반영
        let vatRate = 0;
        if (vatOption === 'BC21' || vatOption === 'BC45') {
            vatRate = 0.11; // 11%
        }
        
        if (vatOption === 'BC21') {
            groups[key].vatAmount += itemAmount * vatRate;
        } else if (vatOption === 'BC45') {
            groups[key].vatAmount2 += itemAmount * vatRate;
        }
        
        groups[key].items.push({
            subOrderId: item.sub_order_id,
            buyerName: formatBuyerColumnDisplay(item.buyer_name, item.buyer_detail),
            fileNo: item.file_no,
            styleNo: item.style_no,
            dyeingNo: item.dyeing_no_short || item.dyeing_no || '',
            rowKey: item.id || `${item.main_order_id}-${item.sub_order_id}`,
            threadName: item.thread_name,
            sizeName: parseSizeFromThreadName(item.thread_name) || item.size_name,
            color: item.color,
            quantity: effectiveQty,
            unitPrice: item.price,
            timeOfArrival: item.time_of_arrival,
            amount: itemAmount
        });
        
        return groups;
    }, {});
    
    const totalAmount = selectedData.reduce((sum, item) => sum + (getPopupQty(item) * (item.price || 0)), 0);
    
    // 납품수량 합계 계산
    const totalQuantity = selectedData.reduce((sum, item) => {
        return sum + getPopupQty(item);
    }, 0);
    
    // 거래처명 (모든 선택된 항목이 같은 거래처인지 확인)
    const vendorNames = [...new Set(allSelectedData.map(item => item.vendor_name).filter(Boolean))];
    const vendorName = vendorNames.length === 0
        ? ''
        : vendorNames.length === 1
            ? vendorNames[0]
            : `${vendorNames.length}개 거래처`;
    
    const currencyGroups = selectedData.reduce((groups, item) => {
        const currency = item.price_currency || 'Unknown';
        if (!groups[currency]) {
            groups[currency] = { count: 0, amount: 0, vatAmount: 0, vatAmount2: 0 };
        }
        groups[currency].count += 1;
        groups[currency].amount += (getPopupQty(item) * (item.price || 0));
        
        // VAT 금액 계산
        const effectiveQty = getPopupQty(item);
        const itemAmount = effectiveQty * (item.price || 0);
        let vatRate = 0;
        if (vatOption === 'BC21' || vatOption === 'BC45') {
            vatRate = 0.11; // 11%
        }
        
        if (vatOption === 'BC21') {
            groups[currency].vatAmount += itemAmount * vatRate;
        } else if (vatOption === 'BC45') {
            groups[currency].vatAmount2 += itemAmount * vatRate;
        }
        
        return groups;
    }, {});

    const formatAmount = (amount) => {
        if (!amount) return '0.00';
        return parseFloat(amount).toFixed(2);
    };

    const isAllRowsSelected = detailRows.length > 0 && selectedRowKeys.length === detailRows.length;

    const handleToggleRow = (rowKey, checked) => {
        setSelectedRowKeys((prev) => {
            if (checked) {
                return prev.includes(rowKey) ? prev : [...prev, rowKey];
            }
            return prev.filter((key) => key !== rowKey);
        });
    };

    const handleToggleAllRows = (checked) => {
        if (checked) {
            setSelectedRowKeys(detailRows.map((row) => row.rowKey));
            return;
        }
        setSelectedRowKeys([]);
    };

    const handleBulkApplyDeliveryDate = () => {
        const bulk = parseDeliveryDateForUi(bulkDeliveryDate);
        if (!bulk || selectedRowKeys.length === 0) return;

        setEditableDeliveryDates((prev) => {
            const updated = { ...prev };
            selectedRowKeys.forEach((rowKey) => {
                updated[rowKey] = bulk;
            });
            return updated;
        });
    };

    const handleConfirmWithDeliveryDates = () => {
        const keySet = new Set(selectedRowKeys);
        const deliveryDateByRowKey = {};
        detailRows.forEach((row) => {
            if (!keySet.has(row.rowKey)) return;
            const deliveryDate = editableDeliveryDates[row.rowKey];
            const d = parseDeliveryDateForUi(deliveryDate);
            if (d) {
                deliveryDateByRowKey[String(row.rowKey)] = d.format('YYYY-MM-DD');
            }
        });
        onConfirm(vatOption, deliveryDateByRowKey, selectedRowKeys);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            PaperProps={{
                sx: {
                    width: '95vw',
                    maxWidth: '1600px',
                    minHeight: '60vh',
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                py: 1.5,
                px: 2.5,
                borderBottom: '1px solid',
                borderColor: 'divider'
            }}>
                <Typography variant="subtitle1" fontWeight="bold">
                    {t('invoice.dialog.title')}
                </Typography>
                {vendorName && (
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                            borderLeft: '1px solid',
                            borderColor: 'divider',
                            pl: 1.25,
                            ml: 0.25
                        }}
                    >
                        {vendorName}
                    </Typography>
                )}
            </DialogTitle>

            <DialogContent sx={{ p: 2.5 }}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="overline" sx={{
                        color: 'text.secondary',
                        fontWeight: 600,
                        letterSpacing: 1,
                        display: 'block',
                        mb: 1
                    }}>
                        {t('invoice.dialog.summary', '발급 요약')}
                    </Typography>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        px: 2,
                        py: 1.25
                    }}>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>{t('invoice.dialog.vatType')}</InputLabel>
                            <Select
                                value={vatOption}
                                onChange={(e) => setVatOption(e.target.value)}
                                label={t('invoice.dialog.vatType')}
                            >
                                <MenuItem value="tax_free">{t('invoice.vat.taxFree')}</MenuItem>
                                <MenuItem value="BC21">{t('invoice.vat.BC21')}</MenuItem>
                                <MenuItem value="BC45">{t('invoice.vat.BC45')}</MenuItem>
                            </Select>
                        </FormControl>

                        <Box sx={{ width: '1px', height: 32, bgcolor: 'divider' }} />

                        {Object.entries(currencyGroups).length === 0 ? (
                            <Typography variant="caption" color="text.disabled" sx={{ py: 1, flex: 1, textAlign: 'center' }}>
                                {t('invoice.error.noItemSelected', '선택된 항목이 없습니다')}
                            </Typography>
                        ) : (
                            <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', gap: 1 }}>
                                {Object.entries(currencyGroups).map(([currency, data]) => {
                                    const sym = getCurrencySymbol(currency);
                                    const vatAmt = vatOption === 'BC21' ? data.vatAmount : vatOption === 'BC45' ? data.vatAmount2 : 0;
                                    const stats = [
                                        {
                                            label: t('invoice.columns.priceCurrency'),
                                            value: currency,
                                            bold: true
                                        },
                                        {
                                            label: t('invoice.dialog.itemCount'),
                                            value: data.count
                                        },
                                        {
                                            label: t('invoice.dialog.totalQuantity'),
                                            value: Number(totalQuantity).toLocaleString()
                                        },
                                        {
                                            label: t('invoice.dialog.currencyAmount'),
                                            value: `${sym} ${formatAmount(data.amount)}`,
                                            bold: true,
                                            color: 'primary.main',
                                            tabular: true
                                        },
                                        {
                                            label: vatOption === 'BC21' ? 'VAT (BC21)' : vatOption === 'BC45' ? 'VAT (BC45)' : 'VAT',
                                            value: `${sym} ${formatAmount(vatAmt)}`,
                                            color: 'text.secondary',
                                            tabular: true
                                        }
                                    ];
                                    return (
                                        <Box
                                            key={currency}
                                            sx={{
                                                display: 'flex',
                                                width: '100%',
                                                alignItems: 'stretch'
                                            }}
                                        >
                                            {stats.map((stat, i) => (
                                                <Box
                                                    key={i}
                                                    sx={{
                                                        flex: 1,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        textAlign: 'center',
                                                        px: 1,
                                                        borderLeft: i === 0 ? 'none' : '1px solid',
                                                        borderColor: 'divider'
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ lineHeight: 1.1, mb: 0.5, letterSpacing: 0.3 }}
                                                    >
                                                        {stat.label}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontWeight: stat.bold ? 700 : 600,
                                                            color: stat.color || 'text.primary',
                                                            fontVariantNumeric: stat.tabular ? 'tabular-nums' : 'normal',
                                                            whiteSpace: 'nowrap',
                                                            lineHeight: 1.2
                                                        }}
                                                    >
                                                        {stat.value}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </Box>
                </Box>

                <Box>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mb: 1,
                        flexWrap: 'wrap'
                    }}>
                        <Typography variant="overline" sx={{
                            color: 'text.secondary',
                            fontWeight: 600,
                            letterSpacing: 1
                        }}>
                            {t('invoice.dialog.details', '발급 상세')}
                        </Typography>
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            color: 'warning.dark'
                        }}>
                            <WarningIcon fontSize="small" />
                            <Typography variant="caption" sx={{ lineHeight: 1.3, fontWeight: 500 }}>
                                {t('invoice.dialog.warning')}
                            </Typography>
                        </Box>
                        <Box sx={{ flex: 1 }} />
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {t('invoice.dialog.bulkApplyDeliveryDate', '선택 행에 일괄 적용')}
                        </Typography>
                        <DatePicker
                            label=""
                            value={bulkDeliveryDate}
                            onChange={(newValue) => setBulkDeliveryDate(parseDeliveryDateForUi(newValue) ?? null)}
                            format={deliveryDateFormat}
                            disabled={isSubmitting}
                            slotProps={{
                                textField: {
                                    size: 'small',
                                    placeholder: deliveryDateFormat,
                                    sx: {
                                        width: 118,
                                        '& .MuiInputBase-root': { fontSize: '0.76rem' },
                                        '& .MuiInputBase-input': {
                                            py: 0.4,
                                            px: 0.75,
                                            fontSize: '0.76rem',
                                            textAlign: 'center'
                                        },
                                        '& .MuiIconButton-root': { p: 0.25 },
                                        '& .MuiSvgIcon-root': { fontSize: '1rem' },
                                        '& .MuiInputAdornment-root': { ml: 0 }
                                    }
                                }
                            }}
                        />
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DoneAllIcon sx={{ fontSize: '0.95rem' }} />}
                            onClick={handleBulkApplyDeliveryDate}
                            disabled={isSubmitting || !bulkDeliveryDate || selectedRowKeys.length === 0}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                                py: 0.25,
                                px: 1,
                                fontSize: '0.78rem',
                                minHeight: 0
                            }}
                        >
                            {t('common.apply', '적용')}
                        </Button>
                    </Box>

                <TableContainer
                    sx={{
                        maxHeight: 'calc(90vh - 320px)',
                        minHeight: 320,
                        overflowX: 'auto',
                        overflowY: 'auto',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        '& .MuiTableCell-root': {
                            py: 0.75,
                            px: 1,
                            fontSize: '0.82rem',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            textAlign: 'center',
                            whiteSpace: 'nowrap',
                            lineHeight: 1.35
                        },
                        '& .MuiTableHead-root .MuiTableCell-root': {
                            py: 1,
                            fontSize: '0.75rem',
                            letterSpacing: 0.4,
                            backgroundColor: '#f5f7fa',
                            color: 'text.secondary',
                            fontWeight: 700,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            position: 'sticky',
                            top: 0,
                            zIndex: 2,
                            whiteSpace: 'nowrap'
                        }
                    }}
                >
                    <Table size="small" stickyHeader sx={{ minWidth: 1280 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 44 }}>
                                    <input
                                        type="checkbox"
                                        checked={isAllRowsSelected}
                                        onChange={(e) => handleToggleAllRows(e.target.checked)}
                                        disabled={isSubmitting || detailRows.length === 0}
                                    />
                                </TableCell>
                                <TableCell sx={{ width: 50 }}>NO</TableCell>
                                <TableCell>STYLE NO</TableCell>
                                <TableCell>FILE NO</TableCell>
                                <TableCell>DYEING NO</TableCell>
                                <TableCell>ITEM</TableCell>
                                <TableCell>SIZE</TableCell>
                                <TableCell>COLOR</TableCell>
                                <TableCell>DELIVERY DATE</TableCell>
                                <TableCell>QTY</TableCell>
                                <TableCell>PRICE</TableCell>
                                <TableCell>AMOUNT</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(() => {
                                let seqNo = 0;
                                return Object.values(invoiceGroups).map((group) =>
                                    group.items.map((item, itemIndex) => {
                                        seqNo += 1;
                                        const currencySymbol = getCurrencySymbol(group.currency);
                                        const isChecked = selectedRowKeys.includes(item.rowKey);
                                        return (
                                        <TableRow
                                            key={`${group.mainOrderId}-${item.subOrderId}-${itemIndex}`}
                                            sx={{
                                                opacity: isChecked ? 1 : 0.45,
                                                backgroundColor: isChecked ? 'inherit' : 'action.hover',
                                                transition: 'opacity 0.15s, background-color 0.15s',
                                                '& td': isChecked ? {} : { color: 'text.disabled' }
                                            }}
                                        >
                                            <TableCell>
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={(e) => handleToggleRow(item.rowKey, e.target.checked)}
                                                    disabled={isSubmitting}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ color: 'text.secondary', fontWeight: 500 }}>{seqNo}</TableCell>
                                            <TableCell>{item.styleNo || '-'}</TableCell>
                                            <TableCell>{item.fileNo || '-'}</TableCell>
                                            <TableCell>{item.dyeingNo || '-'}</TableCell>
                                            <TableCell>{item.threadName || '-'}</TableCell>
                                            <TableCell>{item.sizeName || '-'}</TableCell>
                                            <TableCell>{item.color || '-'}</TableCell>
                                            <TableCell sx={{ p: 0.25 }}>
                                                <DatePicker
                                                    value={parseDeliveryDateForUi(editableDeliveryDates[item.rowKey]) ?? null}
                                                    onChange={(newValue) => {
                                                        setEditableDeliveryDates((prev) => ({
                                                            ...prev,
                                                            [item.rowKey]: parseDeliveryDateForUi(newValue) ?? null
                                                        }));
                                                    }}
                                                    format={deliveryDateFormat}
                                                    disabled={isSubmitting || !isChecked}
                                                    slotProps={{
                                                        textField: {
                                                            size: 'small',
                                                            sx: {
                                                                width: 118,
                                                                '& .MuiInputBase-root': { fontSize: '0.76rem' },
                                                                '& .MuiInputBase-input': {
                                                                    py: 0.4,
                                                                    px: 0.75,
                                                                    fontSize: '0.76rem',
                                                                    textAlign: 'center'
                                                                },
                                                                '& .MuiIconButton-root': { p: 0.25 },
                                                                '& .MuiSvgIcon-root': { fontSize: '1rem' },
                                                                '& .MuiInputAdornment-root': { ml: 0 }
                                                            }
                                                        }
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                                {item.quantity ? Number(item.quantity).toLocaleString() : '-'}
                                            </TableCell>
                                            <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                                {currencySymbol} {formatAmount(item.unitPrice)}
                                            </TableCell>
                                            <TableCell sx={{
                                                fontVariantNumeric: 'tabular-nums',
                                                fontWeight: 600,
                                                color: isChecked ? 'primary.main' : 'text.disabled'
                                            }}>
                                                {currencySymbol} {formatAmount(item.amount)}
                                            </TableCell>
                                        </TableRow>
                                        );
                                    })
                                );
                            })()}
                        </TableBody>
                    </Table>
                </TableContainer>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 2.5, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                <Button
                    onClick={onClose}
                    startIcon={<CloseIcon />}
                    variant="outlined"
                    color="inherit"
                    disabled={isSubmitting}
                >
                    {t('common.cancel')}
                </Button>
                <Button
                    onClick={handleConfirmWithDeliveryDates}
                    startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
                    variant="contained"
                    color="success"
                    disabled={isSubmitting || selectedRowKeys.length === 0}
                    autoFocus
                >
                    {t('invoice.dialog.confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default InvoiceIssuanceDialog; 